'use client';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft } from 'lucide-react';

interface PushedOverlayProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Optional action rendered top-right (e.g. an "Edit" link). */
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Full-screen overlay that slides in from the right and covers the bottom tab bar,
 * matching the Seamigo design's "pushed overlay" navigation pattern. A circular back
 * button pops it; the browser/Android hardware back button does too (via a history entry).
 * On mobile, swiping right from the left edge (iOS-style back gesture) also dismisses it.
 * On md+ it renders as a centered card so desktop stays modal-like.
 */
export default function PushedOverlay({ open, onClose, title, headerRight, children }: PushedOverlayProps) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Swipe-to-dismiss state
  const [dragX, setDragX] = useState(0);
  const isDragging = useRef(false);
  const startX = useRef(0);

  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    const onPop = () => onCloseRef.current();

    // Push a history entry so the hardware/browser back button dismisses the overlay
    // instead of leaving the screen.
    window.history.pushState({ pushedOverlay: true }, '');
    window.addEventListener('keydown', onKey);
    window.addEventListener('popstate', onPop);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('popstate', onPop);
      // If our history entry is still on the stack (closed via button/Escape, not back),
      // remove it so the stack stays balanced.
      if (window.history.state?.pushedOverlay) window.history.back();
    };
  }, [open]);

  // Reset drag when overlay closes
  useEffect(() => {
    if (!open) { setDragX(0); isDragging.current = false; }
  }, [open]);

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    // Only trigger from left edge (iOS-style back gesture zone)
    if (touch.clientX > 30) return;
    startX.current = touch.clientX;
    isDragging.current = true;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current) return;
    const dx = Math.max(0, e.touches[0].clientX - startX.current);
    setDragX(dx);
  };

  const handleTouchEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const threshold = Math.min(100, window.innerWidth * 0.28);
    if (dragX >= threshold) {
      onCloseRef.current();
    }
    setDragX(0);
  };

  if (!open || typeof document === 'undefined') return null;

  const scrimOpacity = dragX > 0 ? Math.max(0, 1 - dragX / 160) : 1;

  return createPortal(
    <div
      className="pushed-overlay-root"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className="pushed-overlay-panel"
        style={{
          transform: dragX > 0 ? `translateX(${dragX}px)` : undefined,
          transition: dragX > 0 ? 'none' : undefined,
          opacity: dragX > 0 ? Math.max(0.7, 1 - dragX / 400) : undefined,
        }}
      >
        <header className="pushed-overlay-header" style={{ opacity: scrimOpacity }}>
          <button type="button" onClick={onClose} className="pushed-overlay-back" aria-label="Back">
            <ChevronLeft size={20} />
          </button>
          <h2 className="pushed-overlay-title">{title}</h2>
          <div className="pushed-overlay-header-right">{headerRight}</div>
        </header>
        <div className="pushed-overlay-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
